<template>
  <section id="services" class="services-section">
    <v-container class="py-16">
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
      </div>

      <div class="text-center mb-16 header-container" data-aos="fade-up">
        <div class="badge-wrapper">
          <span class="premium-badge">
            <span class="badge-shine"></span>
            <v-icon small color="white" class="mr-1">mdi-star</v-icon>
            PREMIUM SERVICES
          </span>
        </div>
        <h2 class="section-title">
          <span class="title-word">Transform</span>
          <span class="title-word accent">Your Space</span>
        </h2>
        <p class="section-description">
          Experience excellence with our comprehensive suite of professional
          services
        </p>

        <div class="title-decoration">
          <div class="decoration-line"></div>
          <div class="decoration-dot"></div>
          <div class="decoration-line"></div>
        </div>
      </div>

      <div class="slider-wrapper">
        <v-btn
          class="scroll-arrow left-arrow"
          icon
          fab
          small
          @click="scrollLeft"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <div
          class="services-slider"
          ref="slider"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
          @touchstart="startDrag"
          @touchmove="onDrag"
          @touchend="endDrag"
        >
          <div
            class="services-track"
            :style="{ transform: `translateX(${translateX}px)` }"
          >
            <div
              v-for="(service, index) in [...services, ...services]"
              :key="index"
              class="service-card-wrapper"
              :style="{ animationDelay: `${(index % services.length) * 0.1}s` }"
            >
              <div
                class="service-card"
                @mouseenter="hoveredIndex = index"
                @mouseleave="hoveredIndex = null"
              >
                <div class="card-border"></div>

                <div class="service-image-container">
                  <div class="image-overlay"></div>
                  <div class="image-pattern"></div>
                  <img
                    :src="service.image"
                    :alt="service.title"
                    class="service-image"
                  />
                  <div class="service-icon-wrapper">
                    <div class="icon-pulse"></div>
                    <div class="icon-background" :class="service.colorClass">
                      <v-icon size="32" color="white" class="service-icon">
                        {{ service.icon }}
                      </v-icon>
                    </div>
                  </div>
                  <div class="popular-badge" v-if="service.popular">
                    <v-icon small color="white">mdi-fire</v-icon>
                    POPULAR
                  </div>
                </div>
                <div class="service-content">
                  <div class="content-header">
                    <h3 class="service-title">
                      {{ getServiceTitle(service.title) }}
                    </h3>
                    <div class="service-number">
                      {{
                        String((index % services.length) + 1).padStart(2, "0")
                      }}
                    </div>
                  </div>
                  <p class="service-description">{{ service.description }}</p>
                  <div class="service-features">
                    <div
                      v-for="(feature, idx) in service.features"
                      :key="idx"
                      class="feature-item"
                    >
                      <v-icon size="14" color="#f46523"
                        >mdi-check-circle</v-icon
                      >
                      <span>{{ feature }}</span>
                    </div>
                  </div>
                </div>
                <div
                  class="card-shine"
                  :class="{ active: hoveredIndex === index }"
                ></div>
                <div class="corner-shine top-left"></div>
                <div class="corner-shine bottom-right"></div>
              </div>
            </div>
          </div>
        </div>

        <v-btn
          class="scroll-arrow right-arrow"
          icon
          fab
          small
          @click="scrollRight"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
      <div class="scroll-indicator">
        <v-icon color="#f46523" class="scroll-icon"
          >mdi-chevron-double-right</v-icon
        >
        <span>Drag to explore more</span>
        <v-icon color="#f46523" class="scroll-icon"
          >mdi-chevron-double-left</v-icon
        >
      </div>
      <HowItWorks />
    </v-container>
  </section>
</template>

<script>
import HowItWorks from "./HowItWorks.vue";

export default {
  name: "ServicesContent",
  components: { HowItWorks },
  data() {
    return {
      hoveredIndex: null,
      isDragging: false,
      startX: 0,
      startY: 0,
      velocity: 0,
      lastX: 0,
      translateX: 0,
      sliderWidth: 0,
      animationFrame: null,
      services: [
        {
          icon: "mdi-lightning-bolt",
          title: "Electrical Works",
          description:
            "Professional electrical installations, repairs, and maintenance with certified expertise.",
          image:
            "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80",
          colorClass: "color-electric",
          popular: true,
          features: ["24/7 Emergency", "Licensed Experts", "Safety Certified"],
        },
        {
          icon: "mdi-pipe-wrench",
          title: "Plumbing",
          description:
            "Comprehensive plumbing solutions for residential and commercial properties.",
          image:
            "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=800&q=80",
          colorClass: "color-water",
          popular: true,
          features: ["Fast Response", "Quality Parts", "Warranty Included"],
        },
        {
          icon: "mdi-air-conditioner",
          title: "Air Conditioner Repair",
          description:
            "Expert AC installation, maintenance, and repair services for optimal comfort.",
          image:
            "https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800&q=80",
          colorClass: "color-air",
          features: ["All Brands", "Energy Efficient", "Same Day Service"],
        },
        {
          icon: "mdi-television",
          title: "Home Appliance Repair",
          description:
            "Quick and reliable repairs for all your home appliances with genuine parts.",
          image:
            "https://images.unsplash.com/photo-1556911073-38141963c9e0?w=800&q=80",
          colorClass: "color-appliance",
          features: ["Genuine Parts", "Expert Team", "Quick Turnaround"],
        },
        {
          icon: "mdi-broom",
          title: "Cleaning Works",
          description:
            "Deep cleaning and sanitization services using eco-friendly products.",
          image:
            "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80",
          colorClass: "color-clean",
          popular: true,
          features: ["Eco-Friendly", "Deep Cleaning", "Trained Staff"],
        },
        {
          icon: "mdi-format-paint",
          title: "Painting",
          description:
            "Interior and exterior painting with premium finishes and color consultation.",
          image:
            "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&q=80",
          colorClass: "color-paint",
          features: ["Free Consultation", "Premium Paint", "Perfect Finish"],
        },
        {
          icon: "mdi-flower",
          title: "Gardening",
          description:
            "Landscape design, maintenance, and garden care for beautiful outdoor spaces.",
          image:
            "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80",
          colorClass: "color-garden",
          features: ["Design Expert", "Plant Care", "Regular Maintenance"],
        },
        {
          icon: "mdi-hammer-wrench",
          title: "Construction",
          description:
            "Complete construction and renovation services with quality craftsmanship.",
          image:
            "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&q=80",
          colorClass: "color-construction",
          features: ["Quality Work", "On-Time Delivery", "Budget Friendly"],
        },
      ],
    };
  },

  methods: {
    getServiceTitle(baseTitle) {
      return baseTitle;
    },

    startDrag(e) {
      cancelAnimationFrame(this.animationFrame);
      this.isDragging = true;
      const clientX = e.pageX || (e.touches && e.touches[0].pageX);
      const clientY = e.pageY || (e.touches && e.touches[0].pageY);
      this.startX = clientX;
      this.startY = clientY;
      this.lastX = this.startX;
      this.velocity = 0;

      if (e.type === "mousedown") e.preventDefault();
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const clientX = e.pageX || (e.touches && e.touches[0].pageX);
      const clientY = e.pageY || (e.touches && e.touches[0].pageY);
      const deltaX = clientX - this.lastX;
      if (e.touches) {
        const deltaY = clientY - this.startY;
        if (Math.abs(clientX - this.startX) > Math.abs(deltaY)) {
          e.preventDefault();
        }
      }

      this.translateX += deltaX;
      this.velocity = deltaX;
      this.lastX = clientX;
      this.applyBoundaries();
    },
    endDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.momentumScroll();
    },
    momentumScroll() {
      const friction = 0.92;
      const threshold = 0.5;
      const step = () => {
        this.translateX += this.velocity;
        this.velocity *= friction;
        this.applyBoundaries();

        if (Math.abs(this.velocity) > threshold) {
          this.animationFrame = requestAnimationFrame(step);
        } else {
          this.velocity = 0;
          this.animate();
        }
      };
      if (Math.abs(this.velocity) > threshold) {
        this.animationFrame = requestAnimationFrame(step);
      } else {
        this.animate();
      }
    },

    animate() {
      if (this.isDragging) return;
      this.translateX -= 1.5;
      this.applyBoundaries();

      this.animationFrame = requestAnimationFrame(this.animate);
    },

    applyBoundaries() {
      const totalContentWidth = this.sliderWidth;

      if (this.translateX < -totalContentWidth) {
        this.translateX += totalContentWidth;
      }
      if (this.translateX > 0) {
        this.translateX -= totalContentWidth;
      }
    },
    scrollLeft() {
      const cardSize = 340 + 32;
      cancelAnimationFrame(this.animationFrame);
      this.translateX = Math.min(0, this.translateX + cardSize);
      this.applyBoundaries();
      setTimeout(() => this.animate(), 300);
    },
    scrollRight() {
      const cardSize = 340 + 32;
      cancelAnimationFrame(this.animationFrame);
      this.translateX = Math.max(-this.sliderWidth, this.translateX - cardSize);
      this.applyBoundaries();
      setTimeout(() => this.animate(), 300);
    },
    calculateSliderWidth() {
      if (this.$refs.slider) {
        const cardWidth = 340;
        const gap = 32;
        this.sliderWidth = (cardWidth + gap) * this.services.length;
      }
    },
  },
  mounted() {
    this.calculateSliderWidth();
    window.addEventListener("resize", this.calculateSliderWidth);
    if (this.$refs.slider) {
      this.$refs.slider.addEventListener("touchstart", this.startDrag, {
        passive: false,
      });
      this.$refs.slider.addEventListener("touchmove", this.onDrag, {
        passive: false,
      });
    }
    this.animate();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateSliderWidth);
    cancelAnimationFrame(this.animationFrame);

    if (this.$refs.slider) {
      this.$refs.slider.removeEventListener("touchstart", this.startDrag);
      this.$refs.slider.removeEventListener("touchmove", this.onDrag);
    }
  },
};
</script>

<style scoped>
.services-section {
  background: #ffffff;
  position: relative;
  overflow: hidden;
  user-select: none;
  padding: 10px 0;
}
.header-container {
  position: relative;
  z-index: 10;
}

.badge-wrapper {
  margin-bottom: 24px;
  animation: fadeInDown 0.8s ease-out;
}

.premium-badge {
  display: inline-flex;
  align-items: center;
  padding: 10px 24px;
  background: linear-gradient(135deg, #f46523 0%, #ff8a50 100%);
  border-radius: 50px;
  color: white;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 2px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(244, 101, 35, 0.4);
  animation: pulse 2s ease-in-out infinite;
}

.badge-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shine 3s infinite;
}

@keyframes shine {
  to {
    left: 100%;
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.section-title {
  font-size: 56px;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.1;
  animation: fadeInUp 1s ease-out;
}

.title-word {
  display: inline-block;
  color: #f46523;
  margin: 0 8px;
  animation: fadeInUp 1s ease-out backwards;
}

.title-word.accent {
  background: linear-gradient(135deg, #f46523 0%, #ff8a50 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation-delay: 0.2s;
}

.section-description {
  font-size: 19px;
  color: black;
  max-width: 650px;
  margin: 0 auto 32px;
  line-height: 1.7;
  animation: fadeInUp 1s ease-out 0.3s backwards;
}

.title-decoration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  animation: fadeIn 1.2s ease-out 0.5s backwards;
}

.decoration-line {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #f46523, transparent);
}

.decoration-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f46523;
  box-shadow: 0 0 20px rgba(244, 101, 35, 0.6);
  animation: dotPulse 2s ease-in-out infinite;
}

@keyframes dotPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
}

.slider-wrapper {
  position: relative;
  margin: 60px 55px;
}

.services-slider {
  overflow: hidden;
  position: relative;
  padding: 3rem 0;
  cursor: grab;
  margin: 0;
}

.services-slider:active {
  cursor: grabbing;
}

.scroll-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  background-color: #f46523 !important;
  color: white !important;
  box-shadow: 0 4px 15px rgba(244, 101, 35, 0.5) !important;
  width: 40px !important;
  height: 40px !important;
}

.left-arrow {
  left: -50px;
}

.right-arrow {
  right: -50px;
}

@media (max-width: 960px) {
  .slider-wrapper {
    margin: 60px 10px;
  }
  .left-arrow,
  .right-arrow {
    display: none;
  }
}

.services-track {
  display: flex;
  gap: 32px;
}

.service-card-wrapper {
  flex: 0 0 auto;
  width: 340px;
  animation: slideIn 0.6s ease-out backwards;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.service-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  height: 100%;
  cursor: pointer;
  border: 1px solid rgba(244, 101, 35, 0.2);
}

@keyframes rotateBorder {
  to {
    transform: rotate(360deg);
  }
}

.service-image-container {
  position: relative;
  height: 220px;
  overflow: hidden;
}

.service-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.9) 100%);
  z-index: 1;
}

.image-pattern {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(244, 101, 35, 0.03) 10px,
    rgba(244, 101, 35, 0.03) 20px
  );
  z-index: 1;
  opacity: 0;
}

.service-icon-wrapper {
  position: absolute;
  bottom: 20px;
  left: 24px;
  z-index: 3;
  width: 64px;
  height: 64px;
}

.icon-pulse {
  position: absolute;
  inset: 0;
  border-radius: 16px;
  background: #f46523;
  opacity: 0.2;
  animation: iconPulse 3s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.15);
    opacity: 0;
  }
}

.icon-background {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.popular-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: linear-gradient(135deg, #ff4444, #ff6b6b);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 4px 16px rgba(255, 68, 68, 0.4);
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}
.service-content {
  padding: 28px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 300px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.service-title {
  font-size: 24px;
  font-weight: 800;
  color: black;
  line-height: 1.3;
  flex: 1;
}

.service-number {
  font-size: 32px;
  font-weight: 900;
  color: rgba(244, 101, 35, 0.2);
  line-height: 1;
}

.service-description {
  font-size: 14px;
  color: black;
  line-height: 1.7;
  margin-bottom: 20px;
  height: 63px;
  overflow: hidden;
}

/* Features List */
.service-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: black;
}

.service-cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: rgba(244, 101, 35, 0.1);
  border: 1px solid rgba(244, 101, 35, 0.3);
  border-radius: 50px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  margin-top: auto;
}

.service-cta::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #f46523, #ff8a50);
  opacity: 0;
}

.cta-text {
  font-size: 13px;
  font-weight: 700;
  color: #f46523;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
}

.cta-icon-wrapper {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f46523;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.cta-arrow {
  color: white !important;
}

.card-shine.active {
  animation: shineEffect 0.8s ease-out;
}

.corner-shine {
  position: absolute;
  width: 100px;
  height: 100px;
  opacity: 0;
  pointer-events: none;
  /* Removed will-change */
}

.corner-shine.top-left {
  top: 0;
  left: 0;
  background: radial-gradient(
    circle at top left,
    rgba(244, 101, 35, 0.3),
    transparent
  );
}

.corner-shine.bottom-right {
  bottom: 0;
  right: 0;
  background: radial-gradient(
    circle at bottom right,
    rgba(244, 101, 35, 0.3),
    transparent
  );
}

.scroll-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 600;
  animation: fadeIn 1.5s ease-out 1s backwards;
}

.scroll-icon {
  animation: slideHorizontal 2s ease-in-out infinite;
}

@keyframes slideHorizontal {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(10px);
  }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
  animation: float 20s infinite ease-in-out;
  pointer-events: none;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: #f46523;
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: #ff8a50;
  bottom: -150px;
  right: -150px;
  animation-delay: 7s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: #f46523;
  top: 50%;
  left: 50%;
  animation-delay: 14s;
}

.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.shape {
  position: absolute;
  opacity: 0.05;
  animation: float 25s infinite ease-in-out;
}

.shape-1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #f46523, #ff8a50);
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  top: 10%;
  left: 5%;
  animation-delay: 0s;
}

.shape-2 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #ff8a50, #f46523);
  border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%;
  bottom: 15%;
  right: 10%;
  animation-delay: 8s;
}

.shape-3 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #f46523, #ff8a50);
  border-radius: 50%;
  top: 50%;
  right: 15%;
  animation-delay: 16s;
}

.shape-4 {
  width: 180px;
  height: 180px;
  background: linear-gradient(135deg, #ff8a50, #f46523);
  border-radius: 30% 70% 70% 30% / 70% 30% 30% 70%;
  bottom: 30%;
  left: 10%;
  animation-delay: 12s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(30px, -30px) rotate(90deg);
  }
  50% {
    transform: translate(-20px, 20px) rotate(180deg);
  }
  75% {
    transform: translate(40px, 10px) rotate(360deg);
  }
}
</style>
